/* @flow */
// typecheck: ignore file
import React from 'react';
import invariant from 'invariant';

import AddGroupOwnerModalConfirm from 'groups/components/AddGroupOwnerModalConfirm.jsx';
import BatchRoleCorrectionModal from 'users/components/BatchRoleCorrectionModal.jsx';
import BlockMemberMenuItem from 'users/components/BlockMemberMenuItem.jsx';
import GetTranscriptMenuItem from 'user/components/GetTranscriptMenuItem.jsx';
import Glyph from 'components/Glyph.jsx';
import Group from 'entities/Group';
import GroupMembership from 'entities/GroupMembership';
import Menu from 'components/Menu.jsx';
import MenuButton from 'components/MenuButton.jsx';
import ModalMenuItem from 'components/ModalMenuItem.jsx';
import RelationshipModal from 'user/components/RelationshipModal.jsx';
import RemoveFromOrgModal from 'organizations/components/RemoveFromOrgModal.jsx';
import RemoveMemberMenuItem from 'groups/components/RemoveMemberMenuItem.jsx';
import ReportOrgUserModal from 'organizations/components/ReportOrgUserModal.jsx';
import ReportUserMenuItem from 'user/components/ReportUserMenuItem.jsx';
import Subscriber from 'entities/Subscriber';
import SubscribeUserMenuItem from 'user/components/SubscribeUserMenuItem.jsx';

type Props = {
  buttonSize?: string,
  className?: string,
  groupMembership?: GroupMembership,
  subscriber: Subscriber,
  groupToAdmin?: Group,
  handleClick?: Function,
  level?: string,
  showViewProfile?: bool,
  tooltip?: string,
  tooltipPlacement?: string,
  showChangeRole?: bool,
  showViewProfile?: bool,
  showCopyToClass?: bool,
  showMakeOwner?: bool,
  showRemoveMember?: bool,
  showBlockMember?: bool,
  showGetTranscript?: bool,
  showRemoveFromSchool?: bool,
  showReportAsUser?: bool,
  showReportAsAdmin?: bool,
  disabled?: bool,
  isAdminView?: bool,
};

class UserActionsMenuButton extends React.Component {
  props: Props;

  static defaultProps = {
    level: 'secondary',
  };

  renderMakeOwnerDialog = () => {
    const { groupMembership, groupToAdmin } = this.props;
    invariant(groupMembership, 'renderMakeOwnerDialog called without a group membership');
    return (
      <AddGroupOwnerModalConfirm
        group={groupToAdmin}
        checkedIds={[groupMembership.getUser().id]}
        userName={groupMembership.getUser().getName()}
      />
    );
  };

  renderChangeRoleModal = () => {
    const { groupMembership, groupToAdmin } = this.props;
    invariant(groupMembership, 'renderChangeRoleModal called without a group membership');
    return (
      <BatchRoleCorrectionModal
        group={groupToAdmin}
        groupMemberships={[groupMembership]}
        onClose={() => { /* TODO */ }}
      />
    );
  };

  renderMenu = () => {
    const { groupMembership, groupToAdmin, subscriber, isAdminView } = this.props;

    return (
      <Menu>
        {this.props.showChangeRole && (
          <ModalMenuItem modal={this.renderChangeRoleModal()}>
            <Glyph name="spreadsheet" />
            <span>
              Change role
            </span>
          </ModalMenuItem>
        )}
        {this.props.showViewProfile &&
          <ModalMenuItem modal={this.renderRelationshipDialog()}>
            <Glyph name="avatar" />
            <span>
              View profile
            </span>
          </ModalMenuItem>
        }
        {this.props.showCopyToClass && <SubscribeUserMenuItem subscriber={subscriber} />}
        {this.props.showMakeOwner &&
          <ModalMenuItem modal={this.renderMakeOwnerDialog()}>
            <Glyph name="crown-stroke" />
            <span>
              Make owner of class
            </span>
          </ModalMenuItem>
        }
        {this.props.showRemoveMember &&
          <RemoveMemberMenuItem
            group={groupToAdmin}
            member={groupMembership}
          />
        }
        {this.props.showBlockMember && groupMembership &&
          <BlockMemberMenuItem
            blocked={groupMembership.isBlocked()}
            memberId={groupMembership.getUserId()}
            memberName={groupMembership.getName()}
          />
        }
        {this.props.showGetTranscript && groupMembership &&
          <GetTranscriptMenuItem
            groupMembership={groupMembership}
            groupToAdmin={groupToAdmin}
            isAdminView={isAdminView}
          />
        }
        {this.props.showRemoveFromSchool &&
          <ModalMenuItem modal={this.renderRemoveFromOrgModal()}>
            <Glyph name="close" />
            <span>
              Remove from school
            </span>
          </ModalMenuItem>
        }
        {this.props.showReportAsUser && <ReportUserMenuItem reportee={subscriber} />}
        {this.props.showReportAsAdmin &&
          <ModalMenuItem modal={this.renderReportOrgUserModal()}>
            <Glyph name="flag" />
            <span>
              Report to Remind
            </span>
          </ModalMenuItem>
        }
      </Menu>
    );
  };

  renderRelationshipDialog = () => {
    const { groupToAdmin, groupMembership } = this.props;
    invariant(groupMembership, 'renderMakeOwnerDialog called without a group membership');
    return (
      <RelationshipModal
        groupToAdmin={groupToAdmin}
        groupMember={groupMembership}
        userId={groupMembership.getUserId()}
      />
    );
  };

  renderRemoveFromOrgModal = () => {
    const { groupToAdmin, groupMembership, isAdminView } = this.props;
    invariant(groupToAdmin, 'renderRemoveFromOrgModal when no group');
    invariant(groupMembership, 'renderRemoveFromOrgModal called without a group membership');
    const org = groupToAdmin.getOrganization();
    invariant(org, 'renderRemoveFromOrgModal failed to get org');
    return (
      <RemoveFromOrgModal
        isAdminView={isAdminView}
        userName={groupMembership.getSubscriber().getName()}
        userIds={[groupMembership.getUserId()]}
        orgName={org.getName()}
        orgId={org.id}
        reportModal={this.renderReportOrgUserModal}
      />
    );
  };

  renderReportOrgUserModal = () => {
    const { groupToAdmin, groupMembership } = this.props;
    invariant(groupToAdmin, 'renderReportOrgUserModal when no group');
    invariant(groupMembership, 'renderReportOrgUserModal called without a group membership');
    const org = groupToAdmin.getOrganization();
    invariant(org, 'renderReportOrgUserModal failed to get org');

    return (
      <ReportOrgUserModal
        userName={groupMembership.getSubscriber().getName()}
        userId={groupMembership.getUserId()}
        orgName={org.getName()}
        orgId={org.id}
        removeModal={this.renderRemoveFromOrgModal}
      />
    );
  };

  render() {
    return (
      <MenuButton
        alignment="right"
        disabled={this.props.disabled}
        className={this.props.className}
        menu={this.renderMenu()}
        size="xsmall"
        onClick={this.props.handleClick}
        level={this.props.level}
        tooltip={this.props.tooltip}
        tooltipPlacement={this.props.tooltipPlacement}>
        <Glyph name="ellipses" solo size={this.props.buttonSize || 'xlarge'} />
      </MenuButton>);
  }
}

export default UserActionsMenuButton;



// WEBPACK FOOTER //
// ./src/user/components/UserActionsMenuButton.jsx